import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { CheckCircle2, KeyRound, ShieldCheck } from "lucide-react";

export default function CreateNewPasswordFromClaim() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [token, setToken] = useState("");
  const [claimId, setClaimId] = useState("");
  const [otpStep, setOtpStep] = useState(true); // if arriving directly with token, skip otp
  const [otpClaimId, setOtpClaimId] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = params.get("token");
    const c = params.get("claim");
    if (t && c) {
      setToken(t);
      setClaimId(c);
      setOtpStep(false); // magic link path — go straight to password creation
    } else if (c) {
      // Only claim id in URL (came from OTP email): pre-fill and stay on OTP step
      setOtpClaimId(c);
    }
  }, [params]);

  const submitOtp = async (e) => {
    e.preventDefault();
    if (!otpClaimId.trim()) { toast.error("Ingresa la referencia del reclamo"); return; }
    if (otpCode.length !== 6) { toast.error("El código tiene 6 dígitos"); return; }
    setLoading(true);
    try {
      await api.post("/auth/claim-account/verify-otp", {
        claim_id: otpClaimId.trim(),
        code: otpCode.trim(),
      });
      toast.success("Correo verificado. Revisa tu bandeja para el enlace de nueva contraseña.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código incorrecto");
    } finally { setLoading(false); }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    if (newPassword !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      await api.post("/auth/claim-account/complete", {
        claim_id: claimId,
        token,
        new_password: newPassword,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo completar la recuperación");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="flex justify-center mb-8"><Logo /></div>
      <div className="bg-[#111] border border-[#262626] rounded-md p-8">
        {done ? (
          <div className="text-center py-6" data-testid="claim-complete-success">
            <CheckCircle2 size={48} className="text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl mb-2">CUENTA RECUPERADA</h1>
            <p className="text-sm text-neutral-400 mb-6">
              Tu correo y contraseña fueron actualizados. Ya puedes iniciar sesión.
            </p>
            <Button
              data-testid="claim-go-login"
              onClick={() => nav("/login")}
              className="bg-[#EF4444] hover:bg-[#DC2626]"
            >
              Ir a iniciar sesión
            </Button>
          </div>
        ) : otpStep ? (
          <>
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck size={22} className="text-[#EF4444]" />
              <h1 className="font-display text-3xl">VERIFICAR CORREO</h1>
            </div>
            <p className="text-neutral-400 text-sm mb-6">
              El admin aprobó tu identidad y enviamos un código de 6 dígitos a tu nuevo correo. Ingrésalo aquí para recibir el enlace final.
            </p>
            <form onSubmit={submitOtp} className="space-y-4">
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Referencia del reclamo</Label>
                <Input
                  data-testid="claim-otp-ref"
                  value={otpClaimId}
                  onChange={(e) => setOtpClaimId(e.target.value)}
                  placeholder="ID completo del reclamo"
                  required
                  className="bg-[#171717] border-[#262626] text-white font-mono text-sm"
                />
                <p className="text-xs text-neutral-500 mt-1">Búscalo al final del email que recibiste al reclamar.</p>
              </div>
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Código de 6 dígitos</Label>
                <Input
                  data-testid="claim-otp-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  className="bg-[#171717] border-[#262626] text-white text-center font-mono text-2xl tracking-[0.5em]"
                />
              </div>
              <Button
                data-testid="claim-otp-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#EF4444] hover:bg-[#DC2626]"
              >
                <BusyLabel busy={loading} text="Verificando…">Verificar código</BusyLabel>
              </Button>
            </form>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-2">
              <KeyRound size={22} className="text-[#EF4444]" />
              <h1 className="font-display text-3xl">NUEVA CONTRASEÑA</h1>
            </div>
            <p className="text-neutral-400 text-sm mb-6">
              Este es el paso final. Elige una nueva contraseña para recuperar el acceso a tu cuenta.
            </p>
            <form onSubmit={submitNewPassword} className="space-y-4">
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Nueva contraseña (mín 8)</Label>
                <Input
                  data-testid="claim-new-pw"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  required
                  className="bg-[#171717] border-[#262626] text-white"
                />
              </div>
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Confirmar</Label>
                <Input
                  data-testid="claim-confirm-pw"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={8}
                  required
                  className="bg-[#171717] border-[#262626] text-white"
                />
              </div>
              <Button
                data-testid="claim-complete-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#EF4444] hover:bg-[#DC2626]"
              >
                <BusyLabel busy={loading} text="Guardando…">Recuperar mi cuenta</BusyLabel>
              </Button>
            </form>
          </>
        )}

        <div className="mt-6 pt-6 border-t border-[#262626] text-sm text-neutral-400 text-center">
          <Link to="/login" className="text-[#EF4444] hover:underline">Volver al inicio de sesión</Link>
        </div>
      </div>
    </div>
  );
}
